import { useCallback, useState, type ReactNode } from 'react';
import {
  Button,
  ConfigProvider,
  Drawer,
  Dropdown,
  Layout,
  Menu,
  Space,
  Typography,
  theme as antdTheme,
} from 'antd';
import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { VENDOIQ_PALETTE, chrome } from '@viq-admin/theme';
import { ThemeToggle } from './ThemeToggle';
import { space, typeScale } from './tokens';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { useAdminContentPadding, useCompactHeader, useNarrowViewport } from './useNarrowViewport';
import { PanelToggle } from './PanelToggle';
import {
  ADMIN_HEADER_HEIGHT,
  PANEL_COLLAPSED_WIDTH,
  PANEL_TRANSITION_MS,
} from './panelLayout';
import './adminLayout.css';

const { Header, Content, Sider } = Layout;

/** Expanded rail width. */
const NAV_WIDTH = 200;
/**
 * The phone menu drawer: wider than the rail so a thumb has room, and so the
 * console's tenant picker (moved in here off the header) fits at its own width.
 */
const NAV_DRAWER_WIDTH = 280;
/**
 * Gap between a nav item's highlight (the gold "you are here" pill, and the
 * hover tint) and the rail's edges. antd's default of 4px left the pill almost
 * touching the rail's right-edge line; 6px gives it room without shrinking the
 * collapsed pill below a comfortable 36px.
 */
const NAV_ITEM_INSET = 6;
const NAV_COLLAPSED_KEY = 'viq-admin.nav.collapsed';
const NAV_TRANSITION = (['width', 'min-width', 'max-width', 'flex-basis'] as const)
  .map((p) => `${p} ${PANEL_TRANSITION_MS}ms ease-in-out`)
  .join(', ');

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    // localStorage throws in private-mode / sandboxed contexts — start expanded.
    return false;
  }
}

function persistCollapsed(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
  } catch {
    // Non-fatal: the rail still toggles, the choice just isn't remembered.
  }
}

export interface AdminNavItem {
  key: string;
  label: ReactNode;
  href: string;
  /** Optional leading icon (antd icon element) shown before the label. */
  icon?: ReactNode;
}

/** The signed-in identity shown in the header account dropdown. */
export interface AdminLayoutUser {
  name?: string;
  email?: string;
  /** Human-readable role label (e.g. "Company Admin", "Super Admin"). */
  roleLabel?: string;
}

export interface AdminLayoutProps {
  /** Brand mark (wordmark img or text) shown at the header left. */
  brand: ReactNode;
  /** Optional badge next to the brand — e.g. a "SUPER-ADMIN" tag identifying the plane. */
  badge?: ReactNode;
  navItems: AdminNavItem[];
  /**
   * Optional renderer for a nav item's clickable element. Defaults to a plain
   * `<a href>` (full navigation). Pass a router-aware renderer (e.g. React
   * Router `<Link>`) for client-side routing without full reloads.
   *
   * `children` is what the link must wrap, and it CHANGES with the rail's
   * state: the label while expanded, the icon once collapsed (antd hides the
   * label there, which would take the link with it). Render `children` rather
   * than reaching for `item.label` directly, or the collapsed rail will look
   * clickable and do nothing.
   */
  renderNavLink?: (item: AdminNavItem, children: ReactNode) => ReactNode;
  /** Active nav key. If omitted, derived from window.location.pathname (longest-prefix match). */
  selectedKey?: string;
  /** Extra header content (e.g. the super-admin "operate-as-tenant" picker). */
  headerExtras?: ReactNode;
  /** Signed-in identity, shown in the right-aligned account dropdown. */
  user?: AdminLayoutUser;
  /** Sign-out handler — invoked from the account dropdown's "Sign out" item. */
  onSignOut?: () => void;
  children: ReactNode;
}

function deriveSelectedKey(navItems: AdminNavItem[]): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const path = window.location.pathname;
  return navItems
    .filter((n) => path === n.href || path.startsWith(`${n.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]?.key;
}

/**
 * Shared admin chrome (header + sider nav + content), identical layout/styling
 * AND behavior across both SPA shells — the customer panel and the super-admin
 * console. Each shell passes its own brand, badge, nav items, header slot, user
 * identity, and sign-out handler; the look and interactions are shared. See
 * ADR 016.
 *
 * The header is brand-navy in BOTH light and dark (not `colorPrimary`, which is
 * the blue accent in dark mode); the sider menu is `theme="dark"` + transparent
 * so it reads on the navy chrome regardless of app mode. The right side is the
 * theme toggle + an account dropdown (name/role → Sign out).
 *
 * The nav rail ("IQ Panel") collapses to icons only, toggled from the control at
 * its top-left and remembered in localStorage. The two SPAs are separate
 * origins, so each remembers its own rail without any extra scoping.
 *
 * On a phone-width viewport (`useNarrowViewport`) there is no rail: a menu
 * button at the header's left opens the IQ Panel as a drawer, which closes once
 * an item is picked. `headerExtras` moves into that drawer too, the account
 * button drops to its icon (the name and role move into its dropdown), and the
 * page padding tightens. The desktop layout is unchanged.
 */
export function AdminLayout({
  brand,
  badge,
  navItems,
  renderNavLink,
  selectedKey,
  headerExtras,
  user,
  onSignOut,
  children,
}: AdminLayoutProps) {
  const active = selectedKey ?? deriveSelectedKey(navItems);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const narrow = useNarrowViewport();
  // Narrow implies compact; a tablet is compact but keeps the rail.
  const compactHeader = useCompactHeader() || narrow;
  const contentPadding = useAdminContentPadding();
  const { token } = antdTheme.useToken();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      return next;
    });
  }, []);

  const navLink = (item: AdminNavItem, content: ReactNode) =>
    renderNavLink ? renderNavLink(item, content) : <a href={item.href}>{content}</a>;

  // Puts an expanded item's icon on the same vertical line as the collapsed
  // rail — and the toggle above it — so the icons stay put as the rail
  // collapses rather than sliding sideways. An item's icon sits past its outer
  // inset and the indent; the icon itself is `fontSize` wide.
  const navInlineIndent = PANEL_COLLAPSED_WIDTH / 2 - NAV_ITEM_INSET - token.fontSize / 2;

  const accountLabel = user?.name ?? user?.email ?? 'Account';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          height: ADMIN_HEADER_HEIGHT,
          background: VENDOIQ_PALETTE.navy,
          padding: narrow ? `0 ${space.md}px` : '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: narrow ? space.sm : space.lg,
        }}
      >
        {narrow && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            aria-label="Open IQ Panel"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            style={{ color: chrome.onNavy, flexShrink: 0 }}
          />
        )}
        <span
          style={{
            color: chrome.onNavy,
            fontWeight: 700,
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            gap: space.md,
          }}
        >
          {brand}
          {badge}
        </span>
        {!narrow && headerExtras}
        <Space style={{ marginLeft: 'auto' }} size={narrow ? space.xs : undefined}>
          <ThemeToggle />
          {onSignOut && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  // No room in a phone or tablet header for who is signed in, so
                  // it heads the menu instead — read-only, not an action.
                  ...(compactHeader
                    ? [
                        {
                          key: 'identity',
                          disabled: true,
                          label: (
                            <Space direction="vertical" size={0}>
                              <Typography.Text strong>{accountLabel}</Typography.Text>
                              {user?.roleLabel && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  {user.roleLabel}
                                </Typography.Text>
                              )}
                            </Space>
                          ),
                        },
                        { type: 'divider' as const },
                      ]
                    : []),
                  { key: 'signout', icon: <LogoutOutlined />, label: 'Sign out', onClick: onSignOut },
                ],
              }}
            >
              {compactHeader ? (
                <Button
                  type="text"
                  icon={<UserOutlined />}
                  aria-label={`Account: ${accountLabel}`}
                  style={{ color: chrome.onNavy }}
                />
              ) : (
                <Button type="text" style={{ color: chrome.onNavy }}>
                  <Space size={6}>
                    <UserOutlined />
                    {accountLabel}
                    {user?.roleLabel && (
                      <Typography.Text style={{ color: chrome.onNavyMuted, fontSize: 12 }}>
                        {user.roleLabel}
                      </Typography.Text>
                    )}
                  </Space>
                </Button>
              )}
            </Dropdown>
          )}
        </Space>
      </Header>
      <Layout>
        {narrow ? (
          <Drawer
            placement="left"
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            width={NAV_DRAWER_WIDTH}
            closable={false}
            rootClassName="viq-nav-drawer"
            styles={{
              body: { padding: 0, display: 'flex', flexDirection: 'column' },
              content: { background: VENDOIQ_PALETTE.navy },
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <PanelToggle
                collapsed={false}
                onToggle={() => setMenuOpen(false)}
                tooltip="Hide IQ Panel"
                tone="navy"
              />
              <span
                aria-hidden
                style={{
                  color: chrome.onNavy,
                  fontSize: typeScale.h5,
                  fontWeight: 600,
                  pointerEvents: 'none',
                }}
              >
                IQ Panel
              </span>
            </div>
            {headerExtras && (
              <div style={{ padding: `${space.xs}px ${space.lg}px ${space.md}px` }}>
                {headerExtras}
              </div>
            )}
            <ConfigProvider theme={{ components: { Menu: { itemMarginInline: NAV_ITEM_INSET } } }}>
              <Menu
                mode="inline"
                theme="dark"
                inlineIndent={navInlineIndent}
                selectedKeys={active ? [active] : []}
                // Picking a page is the end of the drawer's job.
                onClick={() => setMenuOpen(false)}
                style={{ flex: 1, borderRight: 0, background: 'transparent' }}
                items={navItems.map((n) => ({
                  key: n.key,
                  icon: n.icon,
                  label: navLink(n, n.label),
                }))}
              />
            </ConfigProvider>
          </Drawer>
        ) : (
          <Sider
            className="viq-nav-sider"
            width={NAV_WIDTH}
            collapsible
            collapsed={collapsed}
            collapsedWidth={PANEL_COLLAPSED_WIDTH}
            // Our own control sits at the top of the rail instead.
            trigger={null}
            style={{
              transition: reducedMotion ? 'none' : NAV_TRANSITION,
              // The rail's right edge, in both states. Drawn as an inset shadow
              // rather than a border so it takes no width: the collapsed rail
              // stays exactly PANEL_COLLAPSED_WIDTH, keeping its icons and toggle
              // centred. A panel placed beside it (the Batch event rail) shares
              // this line as its left edge instead of drawing a second one.
              boxShadow: `inset -1px 0 0 ${token.colorBorderSecondary}`,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Toggle + panel name, mirroring the Batch event rail's own
                  toggle-then-title row. `overflow: hidden` lets the name slide
                  out of view behind the closing rail instead of wrapping. */}
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, overflow: 'hidden' }}>
                <PanelToggle
                  collapsed={collapsed}
                  onToggle={toggleCollapsed}
                  tooltip={collapsed ? 'Show IQ Panel' : 'Hide IQ Panel'}
                  tone="navy"
                />
                <span
                  // Decorative: the toggle beside it already names the panel to a
                  // screen reader, so this would just be read twice.
                  aria-hidden
                  style={{
                    color: chrome.onNavy,
                    fontSize: typeScale.h5,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    // Not a click target — the toggle is.
                    pointerEvents: 'none',
                    opacity: collapsed ? 0 : 1,
                    transition: reducedMotion
                      ? 'none'
                      : `opacity ${PANEL_TRANSITION_MS}ms ease-in-out`,
                  }}
                >
                  IQ Panel
                </span>
              </div>
              {/* Scoped here, next to the indent math that depends on it. Merges
                  with — doesn't replace — the theme's Menu tokens. */}
              <ConfigProvider theme={{ components: { Menu: { itemMarginInline: NAV_ITEM_INSET } } }}>
                <Menu
                  mode="inline"
                  theme="dark"
                  inlineIndent={navInlineIndent}
                  selectedKeys={active ? [active] : []}
                  style={{ flex: 1, borderRight: 0, background: 'transparent' }}
                  items={navItems.map((n) => ({
                    key: n.key,
                    // The click target follows whatever is actually visible. antd
                    // hides the label on a collapsed inline menu, so a link that
                    // only ever wrapped the label would leave the icon looking
                    // clickable while doing nothing.
                    icon: collapsed ? navLink(n, n.icon) : n.icon,
                    label: collapsed ? n.label : navLink(n, n.label),
                    // Only while collapsed: antd renders `title` as the tooltip for
                    // a collapsed item, but also as a native `title` attribute —
                    // which on the expanded rail would add a second, slower browser
                    // tooltip over every nav item.
                    title: collapsed && typeof n.label === 'string' ? n.label : undefined,
                  }))}
                />
              </ConfigProvider>
            </div>
          </Sider>
        )}
        <Layout style={{ padding: contentPadding }}>
          {/* `minWidth: 0` lets a wide child (a table) scroll inside the page
              instead of stretching it past the viewport. */}
          <Content style={{ minWidth: 0 }}>{children}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
}
